import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
  Download as DownloadIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

const UserDataTemplatesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fileInputRef, setFileInputRef] = useState(null);

  // 템플릿 폼 상태
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    packages: '',  // 쉼표로 구분된 문자열
    runcmd: ''     // 줄바꿈으로 구분된 문자열
  });

  // 데이터 로드
  const loadData = async () => {
    setLoading(true);
    try {
      const response = await apiService.getUserDataTemplates();
      setTemplates(response.data.templates || []);
    } catch (error) {
      enqueueSnackbar('템플릿 목록을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // 폼 초기화
  const resetForm = () => {
    setTemplateForm({
      name: '',
      description: '',
      packages: '',
      runcmd: ''
    });
  };

  // 폼 입력 변경 처리
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setTemplateForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 문자열을 배열로 변환
  const parsePackages = (packagesStr) => {
    if (!packagesStr.trim()) return [];
    return packagesStr.split(',').map(p => p.trim()).filter(p => p);
  };

  const parseRuncmd = (runcmdStr) => {
    if (!runcmdStr.trim()) return [];
    return runcmdStr.split('\n').map(cmd => cmd.trim()).filter(cmd => cmd);
  };

  // 템플릿 생성
  const handleCreateTemplate = async () => {
    if (!templateForm.name.trim()) {
      enqueueSnackbar('템플릿 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }

    setCreating(true);
    try {
      await apiService.createUserDataTemplate({
        name: templateForm.name.trim(),
        description: templateForm.description.trim(),
        packages: parsePackages(templateForm.packages),
        runcmd: parseRuncmd(templateForm.runcmd),
        write_files: [],
        additional_config: {}
      });

      enqueueSnackbar('템플릿이 성공적으로 생성되었습니다.', { variant: 'success' });
      setCreateDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      enqueueSnackbar(`템플릿 생성 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  // 템플릿 수정
  const handleUpdateTemplate = async () => {
    if (!templateForm.name.trim()) {
      enqueueSnackbar('템플릿 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }

    setCreating(true);
    try {
      await apiService.updateUserDataTemplate(selectedTemplate.name, {
        description: templateForm.description.trim(),
        packages: parsePackages(templateForm.packages),
        runcmd: parseRuncmd(templateForm.runcmd)
      });

      enqueueSnackbar('템플릿이 성공적으로 수정되었습니다.', { variant: 'success' });
      setEditDialogOpen(false);
      resetForm();
      setSelectedTemplate(null);
      loadData();
    } catch (error) {
      enqueueSnackbar(`템플릿 수정 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  // 편집 다이얼로그 열기
  const handleOpenEditDialog = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      packages: (template.packages || []).join(', '),
      runcmd: (template.runcmd || []).join('\n')
    });
    setEditDialogOpen(true);
  };

  // 삭제 다이얼로그 열기
  const handleOpenDeleteDialog = (template) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  // 템플릿 삭제
  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    setDeleting(true);
    try {
      await apiService.deleteUserDataTemplate(selectedTemplate.name);
      enqueueSnackbar(`템플릿 '${selectedTemplate.name}'이 삭제되었습니다.`, { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      loadData();
    } catch (error) {
      enqueueSnackbar(`템플릿 삭제 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // JSON 내보내기
  const handleExportTemplates = () => {
    const exportData = {
      export_timestamp: new Date().toISOString(),
      templates: templates.map(template => ({
        name: template.name,
        description: template.description,
        packages: template.packages,
        runcmd: template.runcmd,
        write_files: template.write_files,
        additional_config: template.additional_config
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `userdata-templates-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    enqueueSnackbar('User Data 템플릿이 JSON 파일로 내보내졌습니다.', { variant: 'success' });
  };

  // JSON 가져오기
  const handleImportTemplates = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const importData = JSON.parse(e.target.result);

        if (!importData.templates || !Array.isArray(importData.templates)) {
          throw new Error('올바른 템플릿 데이터 형식이 아닙니다.');
        }

        let successCount = 0;
        let failCount = 0;

        for (const templateData of importData.templates) {
          try {
            await apiService.createUserDataTemplate(templateData);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          enqueueSnackbar(`${successCount}개의 템플릿이 성공적으로 가져와졌습니다.`, { variant: 'success' });
          loadData();
        }

        if (failCount > 0) {
          enqueueSnackbar(`${failCount}개의 템플릿 가져오기에 실패했습니다.`, { variant: 'warning' });
        }

      } catch (error) {
        enqueueSnackbar(`파일 가져오기 실패: ${error.message}`, { variant: 'error' });
      }
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  // 템플릿 아이콘 가져오기
  const getTemplateIcon = (templateName) => {
    const name = templateName.toLowerCase();
    if (name.includes('ml') || name.includes('머신러닝') || name.includes('ai')) return <CodeIcon />;
    if (name.includes('dev') || name.includes('개발')) return <CodeIcon />;
    return <DescriptionIcon />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">User Data 템플릿 관리</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{ mr: 1 }}
          >
            새로고침
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportTemplates}
            disabled={templates.length === 0}
            sx={{ mr: 1 }}
          >
            JSON 내보내기
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef?.click()}
            sx={{ mr: 1 }}
          >
            JSON 가져오기
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setCreateDialogOpen(true);
            }}
          >
            새 템플릿 생성
          </Button>
          <input
            type="file"
            ref={(ref) => setFileInputRef(ref)}
            onChange={handleImportTemplates}
            accept=".json"
            style={{ display: 'none' }}
          />
        </Box>
      </Box>

      {/* 템플릿 카드 뷰 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {templates.map((template) => (
          <Grid item xs={12} sm={6} md={4} key={template.name}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getTemplateIcon(template.name)}
                  <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                    {template.name}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {template.description || '설명 없음'}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Packages: {template.packages?.length || 0}개
                  </Typography>
                  {template.packages && template.packages.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {template.packages.slice(0, 3).map((pkg, idx) => (
                        <Chip key={idx} label={pkg} size="small" />
                      ))}
                      {template.packages.length > 3 && (
                        <Chip label={`+${template.packages.length - 3}`} size="small" variant="outlined" />
                      )}
                    </Box>
                  )}

                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Commands: {template.runcmd?.length || 0}개
                  </Typography>
                </Box>

                {template.created_at && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                    생성: {new Date(template.created_at).toLocaleString('ko-KR')}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Tooltip title="편집">
                    <IconButton onClick={() => handleOpenEditDialog(template)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="삭제">
                    <IconButton onClick={() => handleOpenDeleteDialog(template)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {templates.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            등록된 User Data 템플릿이 없습니다.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            새 템플릿을 생성하여 VM 생성 시 초기 설정을 빠르게 적용하세요.
          </Typography>
        </Paper>
      )}

      {/* 템플릿 생성 다이얼로그 */}
      <Dialog open={createDialogOpen} onClose={() => {
        setCreateDialogOpen(false);
        resetForm();
      }} maxWidth="md" fullWidth>
        <DialogTitle>새 User Data 템플릿 생성</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="템플릿 이름"
                  name="name"
                  value={templateForm.name}
                  onChange={handleFormChange}
                  required
                  helperText="고유한 템플릿 이름을 입력하세요"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="설명"
                  name="description"
                  value={templateForm.description}
                  onChange={handleFormChange}
                  helperText="템플릿의 용도를 설명하세요"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" gutterBottom>
                  설정 내용
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="설치할 패키지"
                  name="packages"
                  value={templateForm.packages}
                  onChange={handleFormChange}
                  placeholder="python3-pip, git, vim, htop"
                  helperText="쉼표(,)로 구분하여 여러 패키지를 입력하세요"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="실행할 명령"
                  name="runcmd"
                  value={templateForm.runcmd}
                  onChange={handleFormChange}
                  placeholder="apt update&#10;apt upgrade -y&#10;systemctl enable docker"
                  helperText="한 줄에 하나씩 실행할 명령을 입력하세요"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            resetForm();
          }}>취소</Button>
          <Button
            onClick={handleCreateTemplate}
            variant="contained"
            disabled={creating || !templateForm.name.trim()}
          >
            {creating ? '생성 중...' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 템플릿 편집 다이얼로그 */}
      <Dialog open={editDialogOpen} onClose={() => {
        setEditDialogOpen(false);
        resetForm();
        setSelectedTemplate(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>User Data 템플릿 편집</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="템플릿 이름"
                  name="name"
                  value={templateForm.name}
                  disabled
                  helperText="템플릿 이름은 변경할 수 없습니다"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="설명"
                  name="description"
                  value={templateForm.description}
                  onChange={handleFormChange}
                  helperText="템플릿의 용도를 설명하세요"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" gutterBottom>
                  설정 내용
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="설치할 패키지"
                  name="packages"
                  value={templateForm.packages}
                  onChange={handleFormChange}
                  placeholder="python3-pip, git, vim, htop"
                  helperText="쉼표(,)로 구분하여 여러 패키지를 입력하세요"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="실행할 명령"
                  name="runcmd"
                  value={templateForm.runcmd}
                  onChange={handleFormChange}
                  placeholder="apt update&#10;apt upgrade -y&#10;systemctl enable docker"
                  helperText="한 줄에 하나씩 실행할 명령을 입력하세요"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            resetForm();
            setSelectedTemplate(null);
          }}>취소</Button>
          <Button
            onClick={handleUpdateTemplate}
            variant="contained"
            disabled={creating || !templateForm.name.trim()}
          >
            {creating ? '수정 중...' : '수정'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>템플릿 삭제 확인</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Typography variant="body1">
              정말 User Data 템플릿 '{selectedTemplate.name}'을 삭제하시겠습니까?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button
            onClick={handleDeleteTemplate}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDataTemplatesPage;
